<template>
  <Layout>
    <PageHeader :title="title" />

    <div class="clearfix mc-btn-group__list mc-btn-group__list--detail">
      <McButtonBack href="/master/rfid" />
    </div>

    <h5 class="mc-general__label mt-4">
      {{ $t('master.form.rfid.readerInformation') }}
    </h5>
    <b-card>
      <div class="d-flex">
        <b-col md="auto">
          <span class="fw-light mc-row-check mt-0">{{ $t('master.form.rfid.readerType') }}</span>
          <div>
            <span class="fw-bold mc-strong__label">{{ sensorName }}</span>
          </div>
        </b-col>
        <b-col md="auto" class="mx-4">
          <span class="fw-light mc-row-check mt-0">{{ $t('master.form.rfid.licensePlate') }}</span>
          <div>
            <span class="fw-bold mc-strong__label">{{ licensePlate }}</span>
          </div>
        </b-col>
      </div>
    </b-card>
    <div class="d-flex justify-content-between">
      <h5 class="mc-general__label">
        {{ $t('global.driver') }}
      </h5>
      <McButton
        variant="secondary-red"
        class="me-2"
        icon="icon-outline_trash"
        :text="$t('global.delete')"
        :disabled="!selectedDeviceIndex.length"
        @click="$refs['modal-delete'].show()"
      />
    </div>
    <!-- Table -->
    <div class="slot-wrapper">
      <div class="table-responsive">
        <McGraphqlEditableTable
          ref="table-ref"
          :fields="fields"
          :apiClient="query"
          :v-show="isTableDataAvailable"
          :canCreate="false"
          :canDelete="false"
          :withCard="true"
          :disabled="isProcessingAuth"
          trackBy="deviceIndex"
          trackStateBy="state"
          @available-change="handleDataAvailable"
          @handleSelectValue="handleSelect"
          @handleInput="handleInput"
          @handleChange="handleChange"
          @handleSubmit="handleSubmit"
        >
          <!-- Slot Index Column -->
          <template #deviceIndex="{ item }">
            {{ item.deviceIndex }}
          </template>

          <!-- Slot RFID Column -->
          <template #rfid="{ item }">
            <div class="d-flex justify-content-between">
              {{ item?.rfidMaster?.rfid ?? '-' }}
              <span v-b-tooltip.bottomright :title="`${$t('master.form.rfid.failedStateTooltip')}`">
                <i v-if="item?.state === 'F'" class="icon-fill_alert-triangle icon-danger" />
              </span>
            </div>
          </template>

          <!-- Slot Driver Column -->
          <template #driver="{ item }">
            {{ item?.rfidMaster?.driver?.fullname ?? '-' }}
          </template>
        </McGraphqlEditableTable>
      </div>
    </div>
    <b-modal
      ref="modal-delete"
      class="mc-modal"
      :title="$t('global.delete')"
      size="md"
      centered
      scrollable
    >
      <div class="card-body p-2">
        <p class="mc-paragraph">
          {{ $t('global.confirmDelete1') }} <strong>{{ selectedDeviceIndex.length }}</strong>
          {{ $t('global.confirmDelete2') }}
        </p>
      </div>
      <template #modal-footer="{ cancel }">
        <div class="d-flex">
          <McButton
            variant="secondary"
            class="me-2"
            :text="$t('global.cancel')"
            @click="cancel()"
          />
          <McButton variant="primary" :text="$t('global.delete')" @click="deleteDriver" />
        </div>
      </template>
    </b-modal>
  </Layout>
</template>
<script>
import appConfig from '@/app.config'
import Layout from '@/router/layouts/main'
import PageHeader from '@/components/page-header'
import { mapGetters } from 'vuex'
import McButton from '@/components/McButton'
import McButtonBack from '@/components/McButtonBack'
import { Helper } from '@/mixin/helper'
import McGraphqlEditableTable from '@/components/McGraphqlEditableTable'
import {
  GET_RFID_DETAIL,
  GET_RFIDS,
  UNASSIGN_RFID_READER,
  ASSIGN_RFID_READER,
} from '@/services/api/master/rfid'

export default {
  page: {
    title: 'Form RFID Reader',
    meta: [{ name: 'description', content: appConfig.description }],
  },
  components: {
    Layout,
    PageHeader,
    McButton,
    McButtonBack,
    McGraphqlEditableTable,
  },
  mixins: [Helper],
  data() {
    return {
      vehicleId: this.$route.params.id,
      isTableDataAvailable: true,
      listRFIDTags: [],
      count: 0,
      sensorName: null,
      licensePlate: null,
      excludeRfids: [],
      selectedRfids: [],
      selectedDeviceIndex: [],
      currentState: '',
    }
  },
  computed: {
    ...mapGetters({
      hasAccess: 'identity/hasAccess',
      hasAccessAll: 'identity/hasAccessAll',
    }),
    title() {
      return `${this.$t('master.rfid.detailReader')}`
    },
    fields() {
      return [
        {
          key: 'deviceIndex',
          label: this.$t('master.form.rfid.deviceIndex'),
          thClass: 'mc-table--40',
          trackBy: 'deviceIndex',
          customColumn: true,
        },
        {
          key: 'rfid',
          label: this.$t('master.form.rfid.rfidTag'),
          thClass: 'table--500',
          isEditable: true,
          type: 'select-resourceful',
          placeholder: `${this.$t('global.form.select')} RFID Tag`,
          trackBy: 'rfid',
          optionsGetter: this.resourcefulQuery.getRFIDTags,
          optionLabelFn: ({ item }) => {
            return item.rfid + ' / ' + (item?.driver?.fullname || '-')
          },
          formatter: (item) => item || '-',
          getInitialObject: (item) => {
            return { rfid: item?.rfidMaster?.rfid, driver: item?.rfidMaster?.driver }
          },
          customValueFn: (item) => {
            return item?.rfidMaster?.rfid
          },
          customColumn: true,
          validation: [['required']],
        },
        {
          key: 'driver',
          label: this.$t('master.form.rfid.driver'),
          thClass: 'mc-table--50',
          customColumn: true,
        },
      ]
    },
    query() {
      return {
        get: async () => {
          const res = await this.$apollo.query({
            query: GET_RFID_DETAIL,
            variables: {
              filter: {
                vehicleId: Number(this.vehicleId),
              },
            },
            fetchPolicy: 'no-cache',
          })

          const data = res.data.getRfidReader.rfidReader
          this.excludeRfids = data
          const sensors = data?.vehicle?.sensor || []
          const immobilizerSensor = sensors.find((sensor) => sensor.sensorCode === 39)
          const identifierSensor = sensors.find((sensor) => sensor.sensorCode === 13)

          if (immobilizerSensor) {
            this.sensorName = 'Immobilizer'
            this.currentState = immobilizerSensor.statusText
          } else if (identifierSensor) {
            this.sensorName = 'Identifier'
            this.currentState = identifierSensor.statusText
          }
          this.licensePlate = data?.vehicle?.licensePlate ?? null

          const requiredIndexes = [1, 2, 3]
          const existingIndexes = new Set(data.rfid2Vehicle.map((item) => item.deviceIndex))

          const sortedRfid2Vehicle = requiredIndexes
            .map((index) => {
              if (existingIndexes.has(index)) {
                return data.rfid2Vehicle.find((item) => item.deviceIndex === index)
              } else {
                return {
                  rfidMaster: null,
                  state: null,
                  deviceIndex: index,
                }
              }
            })
            .sort((a, b) => a.deviceIndex - b.deviceIndex)

          while (sortedRfid2Vehicle.length < 3) {
            const nextIndex = sortedRfid2Vehicle.length + 1
            sortedRfid2Vehicle.push({
              rfidMaster: null,
              state: null,
              deviceIndex: nextIndex,
            })
          }

          this.excludeRfids = sortedRfid2Vehicle
            .filter((item) => item.rfidMaster?.rfid)
            .map((item) => item.rfidMaster.rfid)

          const result = {
            data: sortedRfid2Vehicle,
            pagination: {
              total: 3,
              page: 1,
              show: 10,
            },
          }

          return result
        },
      }
    },
    resourcefulQuery() {
      return {
        getRFIDTags: async (opts) => {
          const search = opts?.search ?? null
          const filter = {
            search: search,
            pagination: {
              page: 1,
              show: 9999,
            },
            excludeRfids: this.excludeRfids.filter((rfid) => rfid !== null),
          }
          const res = await this.$apollo.query({
            fetchPolicy: 'no-cache',
            query: GET_RFIDS,
            variables: {
              filter: filter,
            },
          })
          const data = res.data.getRfids.getRfids
          const result = {
            metadata: {
              total_page: 1,
              total_count: 5,
              count: 5,
              page: 1,
            },
            data: {
              paginated_result: data.data,
            },
          }
          return result
        },
      }
    },
    isProcessingAuth() {
      return this.currentState === 'P'
    },
  },

  async mounted() {
    const res = await this.resourcefulQuery.getRFIDTags()
    this.listRFIDTags = res.data.paginated_result
  },

  methods: {
    refreshTable() {
      this.selectedDeviceIndex = []
      if (this.$refs['table-ref']) {
        this.$refs['table-ref'].handleUnselectAll()
        this.$refs['table-ref'].getData({
          limit: this.$refs['table-ref'].limit,
          page: 1,
          sorts: this.$refs['table-ref'].stringSorts(),
        })
      }
    },
    handleDataAvailable(value) {
      this.isTableDataAvailable = value
    },
    handleSelect(slots) {
      this.selectedDeviceIndex = []
      this.selectedRfids = []

      Object.entries(slots).forEach(([key, value]) => {
        const item = this.$refs['table-ref'].items.find(
          (item) => item.deviceIndex === parseInt(key)
        )
        if (value === true && item.rfidMaster) {
          this.selectedDeviceIndex.push(item.deviceIndex)
          if (item.rfidMaster.rfid) {
            this.selectedRfids.push(item.rfidMaster.rfid)
          }
        }
      })
      this.selectedDeviceIndex = [...new Set(this.selectedDeviceIndex)]
    },
    async handleChange(data) {
      const lastItem = { ...data.item }
      const updateDriver = lastItem?.rfidMaster?.driver?.fullname ?? '-'
      this.$set(this.$refs['table-ref'].items[data.index], 'driver', updateDriver)
    },
    async handleInput(index, item, value) {
      const rfidIndex = this.listRFIDTags.findIndex((tag) => tag.rfid === value)
      const updateDriver = this.listRFIDTags[rfidIndex]?.driver?.fullname ?? '-'
      if (rfidIndex !== -1) {
        this.$set(this.$refs['table-ref'].items[index], 'driver', updateDriver)
      }
    },
    async handleSubmit(data, detailRow) {
      const item = this.$refs['table-ref'].items.find(
        (item) => item.deviceIndex === detailRow.deviceIndex
      )
      if (!data.rfid && item && !item.rfidMaster) {
        await this.deleteDriver(detailRow.deviceIndex)
        return
      }
      const res = await this.$apollo.mutate({
        mutation: ASSIGN_RFID_READER,
        variables: {
          input: {
            deviceIndex: detailRow.deviceIndex,
            rfid: data.rfid ?? item.rfidMaster?.rfid,
            vehicleId: Number(this.vehicleId),
          },
        },
      })
      if (!res.data.assignRfidReader.isSuccessful) {
        this.$mcToast.error(this.$t('global.alert.text.failDeleteDriver'))
        return
      }

      this.$mcToast.info(this.$t('global.alert.text.processAddDriver'))
      const excludeRfid = data.rfid ?? data.rfidMaster?.rfid
      if (excludeRfid) {
        this.excludeRfids.push(excludeRfid)
      }
      const affectedData = [detailRow.deviceIndex]
      this.startRefreshIntervalGeneric('update', affectedData)
    },
    async deleteDriver(deviceIndex) {
      if (typeof deviceIndex === 'number') {
        this.selectedDeviceIndex.push(deviceIndex)
      }
      const res = await this.$apollo.mutate({
        mutation: UNASSIGN_RFID_READER,
        variables: {
          input: {
            deviceIndexs: this.selectedDeviceIndex,
            vehicleId: Number(this.vehicleId),
          },
        },
      })

      if (!res.data.unassignRfidReader.isSuccessful) {
        this.$refs['modal-delete'].hide()
        let alertMessage = res.data.unassignRfidReader.message
        if (alertMessage.includes('reader active with empty tag is forbidden')) {
          alertMessage = this.$t('master.list.rfid.validation.emptyTagForbidden')
        } else {
          alertMessage = this.$t('global.alert.text.failDeleteDriver')
        }
        this.$mcToast.error(alertMessage)
        this.selectedRfids = []
        this.selectedDeviceIndex = []
        this.$refs['table-ref'].handleUnselectAll()
        return
      }

      this.$refs['modal-delete'].hide()
      this.$mcToast.info(
        this.$t('global.alert.text.processDeleteDriver', { total: this.selectedDeviceIndex.length })
      )
      this.excludeRfids = this.excludeRfids.filter((rfid) => !this.selectedRfids.includes(rfid))
      this.startRefreshIntervalGeneric('delete', this.selectedDeviceIndex)
      this.selectedRfids = []
      this.selectedDeviceIndex = []
      this.$refs['table-ref'].handleUnselectAll()
    },
    startRefreshIntervalGeneric(operation, affectedIndexes = []) {
      let count = 0
      const maxAttempts = 6

      const refreshInterval = setInterval(async () => {
        if (count >= maxAttempts) {
          clearInterval(refreshInterval)
          return
        }

        const res = await this.$apollo.query({
          query: GET_RFID_DETAIL,
          variables: {
            filter: {
              vehicleId: Number(this.vehicleId),
            },
          },
          fetchPolicy: 'no-cache',
        })

        const updatedData = res.data.getRfidReader.rfidReader.rfid2Vehicle

        updatedData.forEach((updatedItem) => {
          const itemIndex = this.$refs['table-ref'].items.findIndex(
            (item) => item.deviceIndex === updatedItem.deviceIndex
          )
          if (itemIndex !== -1) {
            this.$set(this.$refs['table-ref'].items, itemIndex, updatedItem)
          }
        })

        const affectedData = updatedData.filter((item) =>
          affectedIndexes.includes(item.deviceIndex)
        )
        const allAffectedProcessed = affectedData.every(
          (item) => item.state === 'S' || item.state === 'F'
        )

        if (allAffectedProcessed) {
          clearInterval(refreshInterval)
          this.handleOperationResult(operation, affectedData, affectedIndexes)
        }

        count++
      }, 3000)
    },
    handleOperationResult(operation, updatedData, affectedIndexes) {
      const affectedData = updatedData.filter((item) => affectedIndexes.includes(item.deviceIndex))
      const successItems = affectedData.filter((item) => item.state === 'S')
      const failedItems = affectedData.filter((item) => item.state === 'F')

      if (operation === 'delete') {
        if (successItems.length > 0) {
          this.$mcToast.success(
            this.$t('global.alert.text.successDeleteDriver', {
              total: successItems.length,
            })
          )
        }
        if (failedItems.length > 0) {
          this.$mcToast.error(
            this.$t('global.alert.text.failDeleteDriver', {
              total: failedItems.length,
            })
          )
        }
      } else if (operation === 'update') {
        if (successItems.length > 0) {
          this.$mcToast.success(
            this.$t('global.alert.text.successAddDriver', { total: successItems.length })
          )
        }
        if (failedItems.length > 0) {
          this.$mcToast.error(
            this.$t('global.alert.text.failedAddDriver', { total: failedItems.length })
          )
        }
      }
    },
  },
}
</script>
